import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { getProvider } from '../utils/getProvider';

interface BaseCall {
  chainId: number;
  address: string;
}

interface StandardCall extends BaseCall {
  abi: ethers.Interface;
  method: string;
  args: any[];
}

interface CalldataCall extends BaseCall {
  calldata: string;
}

type Call = StandardCall | CalldataCall;

interface CacheItem {
  result: any;
  timestamp: number;
}

interface Cache {
  [key: string]: CacheItem;
}

interface ResultItem {
  success: boolean;
  value?: any;
  error?: string;
}

// Global state to avoid duplicate fetches and excessive renders
const GLOBAL_STATE = {
  cache: {} as Cache,
  activeRequests: new Map<string, Promise<ResultItem[]>>(),
  subscribers: new Map<string, Set<(data: ResultItem[] | undefined) => void>>(),
  intervals: new Map<string, NodeJS.Timeout>(),
  lastResults: new Map<string, ResultItem[] | undefined>()
};

// Helper to check if a call is a standard call
const isStandardCall = (call: Call): call is StandardCall => {
  return 'method' in call && 'args' in call && 'abi' in call;
};

// Stable hash function for a call to use as key
const getCallHash = (call: Call): string => {
  if (isStandardCall(call)) {
    return `${call.chainId}-${call.address}-${call.method}-${JSON.stringify(call.args)}`;
  } else {
    return `${call.chainId}-${call.address}-${call.calldata}`;
  }
};

// Generate hash for an array of calls
const getCallsHash = (calls: Call[] | null): string => {
  if (!calls || calls.length === 0) return '';
  return calls.map(getCallHash).sort().join('|');
};

// Execute a single call
const executeCall = async (call: Call): Promise<any> => {
  const provider = getProvider(call.chainId);
  
  if (isStandardCall(call)) {
    const contract = new ethers.Contract(call.address, call.abi, provider);
    return await contract[call.method](...call.args);
  } else {
    return await provider.call({
      to: call.address,
      data: call.calldata,
    });
  }
};

// Fetch data for a set of calls
const fetchData = async (
  callsHash: string, 
  calls: Call[], 
  useCache: boolean = true, 
  forceUpdate: boolean = false
): Promise<ResultItem[]> => {
  try {
    const now = Date.now();
    const results: ResultItem[] = [];
    
    for (const call of calls) {
      const callHash = getCallHash(call);
      
      if (!forceUpdate && useCache && GLOBAL_STATE.cache[callHash] && 
          now - GLOBAL_STATE.cache[callHash].timestamp < 600000) {
        results.push({ success: true, value: GLOBAL_STATE.cache[callHash].result });
      } else {
        try {
          const result = await executeCall(call);
          GLOBAL_STATE.cache[callHash] = { result, timestamp: now };
          results.push({ success: true, value: result });
        } catch (error) {
          results.push({ 
            success: false, 
            error: error instanceof Error ? error.message : 'An unknown error occurred' 
          });
        }
      }
    }
    
    return results;
  } catch (error) {
    console.error("Error in fetchData:", error);
    throw error;
  }
};

// Get or create a request for a specific set of calls
const getOrCreateRequest = (
  callsHash: string, 
  calls: Call[], 
  useCache: boolean
): Promise<ResultItem[]> => {
  if (GLOBAL_STATE.activeRequests.has(callsHash)) {
    return GLOBAL_STATE.activeRequests.get(callsHash)!;
  }
  
  const request = fetchData(callsHash, calls, useCache);
  GLOBAL_STATE.activeRequests.set(callsHash, request);
  
  // When request completes, notify subscribers and clean up
  request
    .then(results => {
      GLOBAL_STATE.lastResults.set(callsHash, results);
      const subscribers = GLOBAL_STATE.subscribers.get(callsHash);
      if (subscribers) {
        subscribers.forEach(callback => callback(results));
      }
    })
    .catch(error => {
      console.error("Request failed:", error);
      const subscribers = GLOBAL_STATE.subscribers.get(callsHash);
      if (subscribers) {
        subscribers.forEach(callback => callback(undefined));
      }
    })
    .finally(() => {
      GLOBAL_STATE.activeRequests.delete(callsHash);
    });
    
  return request;
};

// Setup polling for a specific set of calls
const setupPolling = (
  callsHash: string, 
  calls: Call[], 
  pollInterval: number
): void => {
  // Clear any existing interval
  if (GLOBAL_STATE.intervals.has(callsHash)) {
    clearInterval(GLOBAL_STATE.intervals.get(callsHash)!);
  }
  
  // Only set up interval if we have subscribers and a valid interval
  if (pollInterval > 0 && 
      GLOBAL_STATE.subscribers.has(callsHash) && 
      GLOBAL_STATE.subscribers.get(callsHash)!.size > 0) {
      
    const intervalId = setInterval(() => {
      // Only fetch if we still have subscribers
      if (GLOBAL_STATE.subscribers.has(callsHash) && 
          GLOBAL_STATE.subscribers.get(callsHash)!.size > 0) {
        // Force update on polling
        getOrCreateRequest(callsHash, calls, false);
      } else {
        // Clean up if no subscribers left
        clearInterval(GLOBAL_STATE.intervals.get(callsHash)!);
        GLOBAL_STATE.intervals.delete(callsHash);
      }
    }, pollInterval);
    
    GLOBAL_STATE.intervals.set(callsHash, intervalId);
  }
};

// Subscribe to updates for a specific set of calls
const subscribe = (
  callsHash: string, 
  callback: (data: ResultItem[] | undefined) => void
): () => void => {
  if (!GLOBAL_STATE.subscribers.has(callsHash)) {
    GLOBAL_STATE.subscribers.set(callsHash, new Set());
  }
  
  GLOBAL_STATE.subscribers.get(callsHash)!.add(callback);
  
  // Return unsubscribe function
  return () => {
    const subscribers = GLOBAL_STATE.subscribers.get(callsHash);
    if (subscribers) {
      subscribers.delete(callback);
      if (subscribers.size === 0) {
        GLOBAL_STATE.subscribers.delete(callsHash);
        
        if (GLOBAL_STATE.intervals.has(callsHash)) {
          clearInterval(GLOBAL_STATE.intervals.get(callsHash)!);
          GLOBAL_STATE.intervals.delete(callsHash);
        }
      }
    }
  };
};

export default function useChainCalls(
  calls: Call[] | null, 
  useCache: boolean = true, 
  pollInterval: number = 600000
): ResultItem[] | undefined {
  const [data, setData] = useState<ResultItem[] | undefined>(undefined);

  useEffect(() => {
    if (!calls || calls.length === 0) {
      setData(undefined);
      return () => {};
    }
    
    const callsHash = getCallsHash(calls);
    
    if (GLOBAL_STATE.lastResults.has(callsHash)) {
      setData(GLOBAL_STATE.lastResults.get(callsHash));
    } else {
      setData(undefined);
    }
    
    getOrCreateRequest(callsHash, calls, useCache);
    const unsubscribe = subscribe(callsHash, setData);
    
    setupPolling(callsHash, calls, pollInterval);
    
    return unsubscribe;
  }, [JSON.stringify(calls), useCache, pollInterval]);
  
  return data;
}